import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldAlert,
  RefreshCw,
  Search,
  Check,
  X,
  Clock,
  Ban,
  Mail,
  Loader2,
  Eye,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/EmptyState";
import { api } from "@/lib/api";

const STATUS_META = {
  pending_verification: {
    label: "Awaiting email verification",
    icon: Mail,
    className: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
  },
  pending_review: {
    label: "Pending review",
    icon: Clock,
    className: "bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200",
  },
  completed: {
    label: "Completed",
    icon: Check,
    className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
  },
  rejected: {
    label: "Rejected",
    icon: Ban,
    className: "bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200",
  },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || {
    label: status,
    icon: Clock,
    className: "bg-muted text-muted-foreground",
  };
  const Icon = meta.icon;
  return (
    <Badge
      variant="outline"
      className={`gap-1 border-transparent ${meta.className}`}
      data-testid={`status-${status}`}
    >
      <Icon className="size-3" />
      {meta.label}
    </Badge>
  );
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return dt;
  }
}

export default function DataDeletionRequestsPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending_review | completed | rejected | pending_verification
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null); // request object for details dialog
  const [confirmApprove, setConfirmApprove] = useState(null); // request pending approval
  const [rejectFor, setRejectFor] = useState(null); // request pending rejection
  const [rejectNote, setRejectNote] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/data-deletion");
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.userMessage || "Failed to load deletion requests");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return requests
      .filter((r) => (filter === "all" ? true : r.status === filter))
      .filter((r) =>
        !s
          ? true
          : (r.email && r.email.toLowerCase().includes(s)) ||
            (r.name && r.name.toLowerCase().includes(s)),
      );
  }, [requests, filter, search]);

  const counts = useMemo(() => {
    const c = { all: requests.length };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  const doApprove = async () => {
    if (!confirmApprove) return;
    setActionBusy(true);
    try {
      const { data } = await api.post(
        `/admin/data-deletion/${confirmApprove.id}/approve`,
        { note: null },
      );
      toast.success(
        data?.stats?.user_existed
          ? "Approved — user data erased"
          : "Approved — no matching user existed",
      );
      setConfirmApprove(null);
      await load(true);
    } catch (err) {
      toast.error(err.userMessage || "Failed to approve");
    } finally {
      setActionBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    if (!rejectNote.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setActionBusy(true);
    try {
      await api.post(`/admin/data-deletion/${rejectFor.id}/reject`, {
        note: rejectNote.trim(),
      });
      toast.success("Request rejected");
      setRejectFor(null);
      setRejectNote("");
      await load(true);
    } catch (err) {
      toast.error(err.userMessage || "Failed to reject");
    } finally {
      setActionBusy(false);
    }
  };

  const filterTabs = [
    { key: "all", label: "All" },
    { key: "pending_review", label: "Pending review" },
    { key: "pending_verification", label: "Unverified" },
    { key: "completed", label: "Completed" },
    { key: "rejected", label: "Rejected" },
  ];

  return (
    <>
      <PageHeader
        title="Data Deletion Requests"
        description="NDPA right-to-erasure requests submitted via /unsubscribe. Approve to permanently delete matched user data."
      />

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex flex-wrap gap-1.5">
          {filterTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`filter-${t.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                filter === t.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-muted border-border/70 text-muted-foreground"
              }`}
            >
              {t.label}
              <span
                className={`ml-0.5 text-[10px] rounded-full px-1.5 py-0.5 ${
                  filter === t.key
                    ? "bg-primary-foreground/20"
                    : "bg-muted-foreground/10"
                }`}
              >
                {counts[t.key] || 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by email or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-full sm:w-64"
              data-testid="ddr-search-input"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => load(true)}
            disabled={refreshing}
            aria-label="Refresh"
            data-testid="ddr-refresh-button"
          >
            {refreshing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
          </Button>
        </div>
      </div>

      <Card className="border-border/70 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-10 w-full shimmer rounded" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={ShieldAlert}
              title="No requests match this view"
              description="When users submit an erasure request from the public /unsubscribe page, it will appear here for admin review."
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Requested</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Linked user</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`ddr-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmt(r.requested_at)}
                  </TableCell>
                  <TableCell className="font-medium">{r.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.name || "—"}
                  </TableCell>
                  <TableCell>
                    {r.linked_user_id ? (
                      <Badge variant="outline" className="text-xs">Matched</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">No match</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDetail(r)}
                        data-testid={`ddr-view-${r.id}`}
                      >
                        <Eye className="size-3.5 mr-1" /> View
                      </Button>
                      {(r.status === "pending_review" ||
                        r.status === "pending_verification") && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setConfirmApprove(r)}
                            data-testid={`ddr-approve-${r.id}`}
                          >
                            <Check className="size-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectFor(r)}
                            data-testid={`ddr-reject-${r.id}`}
                          >
                            <X className="size-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Details dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="ddr-detail-dialog">
          <DialogHeader>
            <DialogTitle>Deletion request</DialogTitle>
            <DialogDescription>
              Full details for this NDPA erasure request.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <dl className="text-sm space-y-2.5">
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="col-span-2"><StatusBadge status={detail.status} /></dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Email</dt>
                <dd className="col-span-2 font-medium break-all">{detail.email}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Name</dt>
                <dd className="col-span-2">{detail.name || "—"}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Reason</dt>
                <dd className="col-span-2 whitespace-pre-line">{detail.reason || "—"}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Linked user</dt>
                <dd className="col-span-2 font-mono text-xs">{detail.linked_user_id || "—"}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Requested</dt>
                <dd className="col-span-2">{fmt(detail.requested_at)}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Verified</dt>
                <dd className="col-span-2">{fmt(detail.verified_at)}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">Resolved</dt>
                <dd className="col-span-2">{fmt(detail.resolved_at)}</dd>
              </div>
              {detail.admin_note && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-muted-foreground">Admin note</dt>
                  <dd className="col-span-2 whitespace-pre-line">{detail.admin_note}</dd>
                </div>
              )}
              {detail.deleted_stats && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-muted-foreground">Erased</dt>
                  <dd className="col-span-2 text-xs">
                    User: {detail.deleted_stats.user_existed ? "yes" : "no"} · Comments removed:{" "}
                    {detail.deleted_stats.deleted_comments}
                  </dd>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">IP / UA</dt>
                <dd className="col-span-2 text-xs text-muted-foreground break-all">
                  {detail.ip || "—"} · {detail.user_agent || "—"}
                </dd>
              </div>
            </dl>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirmation */}
      <AlertDialog
        open={!!confirmApprove}
        onOpenChange={(o) => !o && setConfirmApprove(null)}
      >
        <AlertDialogContent data-testid="ddr-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve and erase data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the account, comments, likes and OTP artefacts
              linked to <span className="font-semibold">{confirmApprove?.email}</span> (payment
              history is anonymised, not deleted). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doApprove}
              disabled={actionBusy}
              data-testid="ddr-approve-confirm"
            >
              {actionBusy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
              Erase data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject dialog */}
      <Dialog
        open={!!rejectFor}
        onOpenChange={(o) => {
          if (!o) {
            setRejectFor(null);
            setRejectNote("");
          }
        }}
      >
        <DialogContent data-testid="ddr-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject request</DialogTitle>
            <DialogDescription>
              The requester's data will remain intact. This note is logged and shown internally
              only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-note">Reason for rejection</Label>
            <Textarea
              id="reject-note"
              rows={4}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. Duplicate request; identity could not be verified…"
              data-testid="ddr-reject-note"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectFor(null);
                setRejectNote("");
              }}
              disabled={actionBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={doReject}
              disabled={actionBusy || !rejectNote.trim()}
              data-testid="ddr-reject-confirm"
            >
              {actionBusy ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
